import { useAuth } from '@/auth/AuthContext';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { DomainSelector } from '@/components/DomainSelector';
import { useTheme } from '@/theme/useTheme';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { useState } from 'react';

/* Global top bar (brief §5.1): workspace switcher · command-palette trigger ·
 * notifications · copilot toggle · theme/density · user menu.
 * Mobile (< lg): a hamburger opens the nav drawer; density/theme/notifications
 * collapse into the user menu; tap targets grow to >= 44px. Desktop (>= lg) is
 * byte-equivalent to the pre-responsive bar. */
export function TopBar({
  onOpenPalette,
  onToggleCopilot,
  onOpenNav,
  copilotOpen,
}: {
  onOpenPalette: () => void;
  onToggleCopilot: () => void;
  onOpenNav: () => void;
  copilotOpen: boolean;
}) {
  const { user, logout } = useAuth();
  const { toggleTheme, toggleDensity, theme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border-subtle bg-surface px-2 max-lg:pl-[max(0.5rem,env(safe-area-inset-left))] max-lg:pr-[max(1rem,env(safe-area-inset-right))] lg:gap-3 lg:px-3">
      {/* mobile-only hamburger → nav drawer */}
      <button
        onClick={onOpenNav}
        aria-label="Open navigation"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-text-secondary hover:bg-hover hover:text-text-primary lg:hidden"
      >
        <span aria-hidden className="text-lg">☰</span>
      </button>

      <WorkspaceSwitcher />

      {/* active-domain selector - desktop bar variant (Phase 2, Lane 2A).
          Hidden on mobile (it lives in the nav drawer there); renders
          nothing for 0 domains and a static label for a single domain. */}
      <DomainSelector />

      {/* command palette trigger */}
      <button
        onClick={onOpenPalette}
        className="ml-1 flex h-9 min-w-0 max-w-md flex-1 items-center gap-2 rounded-md border border-border-default bg-canvas px-3 text-sm text-text-tertiary hover:border-border-strong max-lg:h-11 lg:ml-2 lg:min-w-[220px]"
      >
        <span aria-hidden>⌕</span>
        <span className="flex-1 truncate text-left">Search or ask…</span>
        <kbd className="hidden rounded border border-border-default px-1.5 py-0.5 text-[10px] sm:inline-block">⌘K</kbd>
      </button>

      <div className="ml-auto flex items-center gap-1">
        <button
          onClick={toggleDensity}
          title="Toggle density"
          aria-label="Toggle density"
          className="hidden h-9 w-9 items-center justify-center rounded-md text-text-tertiary hover:bg-hover hover:text-text-primary lg:flex"
        >
          <span aria-hidden>▤</span>
        </button>
        <button
          onClick={toggleTheme}
          title="Toggle theme"
          aria-label="Toggle theme"
          className="flex h-9 w-9 items-center justify-center rounded-md text-text-tertiary hover:bg-hover hover:text-text-primary max-lg:h-11 max-lg:w-11"
        >
          <span aria-hidden>{theme === 'dark' ? '☀' : '☾'}</span>
        </button>
        <button
          type="button"
          disabled
          aria-disabled="true"
          aria-label="Notifications - none yet"
          title="Notifications - nothing yet"
          className="relative hidden h-9 w-9 items-center justify-center rounded-md text-text-tertiary opacity-60 lg:flex"
        >
          <span aria-hidden>◔</span>
        </button>
        <button
          onClick={onToggleCopilot}
          title="Toggle copilot"
          aria-label={copilotOpen ? 'Hide copilot' : 'Show copilot'}
          aria-pressed={copilotOpen}
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-md transition-colors max-lg:h-11 max-lg:w-11',
            copilotOpen ? 'bg-ai/10 text-ai' : 'text-text-tertiary hover:bg-hover hover:text-text-primary',
          )}
        >
          <span aria-hidden>✧</span>
        </button>

        <div className="relative ml-1">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Account menu"
            aria-expanded={menuOpen}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-inset text-sm font-semibold text-text-primary hover:bg-hover max-lg:h-11 max-lg:w-11"
          >
            {user?.name.slice(0, 1) ?? '?'}
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} aria-hidden />
              <div className="absolute right-0 z-20 mt-1 w-56 rounded-lg border border-border-default bg-surface p-1.5 shadow-lg animate-fade-in">
                <div className="px-2 py-1.5">
                  <div className="text-sm font-medium text-text-primary">{user?.name}</div>
                  <div className="truncate text-xs text-text-tertiary">{user?.email}</div>
                </div>
                {/* On mobile, density lives here (its toolbar button hides < lg). Theme lives on the header icon. */}
                <div className="my-1 h-px bg-border-subtle lg:hidden" />
                <button
                  onClick={() => {
                    toggleDensity();
                  }}
                  className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm text-text-secondary hover:bg-hover lg:hidden"
                >
                  <span>Density</span>
                  <span aria-hidden className="text-text-tertiary">▤</span>
                </button>
                <div className="my-1 h-px bg-border-subtle" />
                <Link to="/settings" onClick={() => setMenuOpen(false)} className="block rounded-md px-2 py-1.5 text-sm text-text-secondary hover:bg-hover">
                  Settings
                </Link>
                <Link to="/settings?tab=Security" onClick={() => setMenuOpen(false)} className="block rounded-md px-2 py-1.5 text-sm text-text-secondary hover:bg-hover">
                  Change password
                </Link>
                <button
                  onClick={() => void logout()}
                  className="block w-full rounded-md px-2 py-1.5 text-left text-sm text-danger hover:bg-hover"
                >
                  Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
